import { useState } from 'react';
import type { AnyBlockField, BlockField } from '../api';
import { ImageField } from './image-field';

/**
 * 区块属性表单（P1-10）。
 *
 * 之前是"遍历 props 生成输入框"：字段名直接显示成 `imageUrl`、`contentAlign`，
 * 枚举要靠手输（输错了保存时才知道），数组根本没有增删入口（FAQ 只能改种子里的那两条），
 * 可空字段也没法清空。这四点合起来，客户实际上只能改"标题"这种一眼能看懂的字段。
 *
 * 现在按服务端下发的字段描述符渲染：中文标签、下拉框、数字范围、图片预览、
 * 列表增删都在。**枚举与范围只影响输入体验，合法性的唯一真源仍是服务端的 schema** ——
 * 描述符写漏了字段，保存时照样会被拒，不会把坏数据写进去。
 *
 * 描述符缺失的区块（将来新加的块如果忘了写）退回通用渲染，不至于打不开。
 */

type Props = Record<string, unknown>;

/**
 * 宿主在运行时补进来的选项（对应描述符里的 `optionsSource`）。
 *
 * 协议不可能知道某个客户有哪些分类，而让客户手抄 slug 几乎是必错题：
 * 抄错了区块就是空的，界面上还看不出为什么。所以"有哪些可选值"这件事
 * 由控制台在渲染时提供，协议只负责说"这个字段的选项要找分类要"。
 */
export interface FieldChoices {
  /** 当前站点的分类：value 用 slug（协议存的就是 slug），label 用显示名 */
  category?: Array<{ value: string; label: string }>;
  /**
   * 当前主题声明的菜单分组（`supports.navLocations`）。
   *
   * 菜单项本身在「导航菜单」页，这里只选"用哪一组"。选项来自主题，
   * 控制台不能写死 —— 有的主题只有一个 `primary`，有的还会给 `secondary`。
   */
  navLocation?: Array<{ value: string; label: string }>;
  /**
   * 当前站点的「资料」（PDF/压缩包，见「资料库」）：value 用资料 uuid。
   *
   * 和分类一样，协议只知道"这里要选一份资料"，不知道客户上传了哪些；
   * 让客户手抄 uuid 是不可能的，所以列表由控制台在渲染时补进来。
   */
  resource?: Array<{ value: string; label: string }>;
  /**
   * 媒体库的落点上下文。给了它，`image` 字段才会出现「从媒体库选择」——
   * 单测与"没有站点"的场景不给，图片字段就退化成纯地址输入。
   *
   * 放在 choices 里而不是给 BlockPropsEditor 加参数：图片字段可能嵌在
   * 列表项里（`items[].imageUrl`、`slides[].imageUrl`），逐层透传一个独立
   * 参数要在三层组件签名上重复，而 choices 本来就一路传到底。
   */
  media?: {
    tenantId: string;
    siteId: string;
    /**
     * 这个租户能不能用 AI 生图（服务端认 `site:update`）。
     *
     * 前端先藏按钮，不是替服务端做鉴权：editor 点进去也只会拿到 403，
     * 而"点一下才发现不行"比"根本看不到"更像坏了。
     */
    canGenerateArt?: boolean;
  };
}

/**
 * 解析一个 select 的可选项。
 *
 * 返回 null 表示"拿不到列表"，调用方要退回文本输入 —— 给一个空下拉更糟：
 * 客户会看到「（未设置）」加一个空白列表，结论是"这个站没有分类"，
 * 于是去新建一个已经存在的分类。
 */
function resolveOptions(
  field: {
    optionsSource?: 'category' | 'navLocation';
    options?: Array<{ value: string; label: string }>;
  },
  choices?: FieldChoices,
): Array<{ value: string; label: string }> | null {
  if (field.optionsSource === 'category') {
    const list = choices?.category;
    return list && list.length > 0 ? list : null;
  }
  if (field.optionsSource === 'navLocation') {
    /*
     * 读不到主题声明就退回文本输入（null），不退回写死的那几个：
     * 客户的主题可能只有一个分组，给一份"猜出来的"多选项，
     * 他会选到一个库里有、前台却取不到的组，菜单又空掉。
     */
    const list = choices?.navLocation;
    return list && list.length > 0 ? list : null;
  }
  return field.options ?? [];
}

function asString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

/**
 * 下拉框的当前值。
 *
 * `numeric` 的 select（`columns: 3` 这种列数选择）在协议里存的是**数字**，
 * 而 option 的 value 天生是字符串。要是不归一，`asString(3)` 得到空串，
 * 下拉永远显示「（未设置）」—— 客户选了「3 张」、保存成功，回来一看还是
 * 未设置，结论是"这个选不了"。归一成字符串后两边才能对上。
 */
function asOptionValue(value: unknown): string {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return asString(value);
}

function asNumber(value: unknown, fallback = 0): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function FieldShell({
  field,
  children,
  action,
}: {
  field: { key?: string; label: string; hint?: string };
  children: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    /*
     * `data-field` 不参与样式，只给宿主做事件委托：客户点到某个输入框时，
     * 外层靠 `closest('[data-field]')` 认出"他在填哪个字段"，好在位置示意图上
     * 高亮对应部位（见 pages/shell-page.tsx）。不用 context 传是因为这个编辑器
     * 还服务着「页面内容」，那边不需要这个信息 —— 加个可选 prop 会污染整条链路。
     */
    <div className="bfield" data-field={field.key}>
      <div className="bfield__head">
        <span className="bfield__label">{field.label}</span>
        {action}
      </div>
      {children}
      {field.hint ? <p className="bfield__hint">{field.hint}</p> : null}
    </div>
  );
}

/**
 * 单个标量字段。
 *
 * 只接 `BlockField`（不含 stringList / list）—— 数组类字段统一由 `FieldControl`
 * 分派。这个边界以前靠"外层都记得分派"来维持，而对象数组的**条目内部**
 * 用的是 `ScalarField` 直连，于是 `tabs[].bullets` 这种嵌在条目里的字符串数组
 * 掉进了兜底的文本框：框里空着（数组转不成字符串），客户一敲就把
 * `["批次之间品质稳定"]` 写成了 `"按照客户需求定制"`。
 * 形状一坏，整段在渲染时就过不了 schema，被降级成主题自带的英文演示文案 ——
 * 客户看到的是"填着填着变英文了"，而界面上没有任何地方指向这个字段。
 */
function ScalarField({
  field,
  value,
  onChange,
  choices,
}: {
  field: BlockField;
  value: unknown;
  onChange: (next: unknown) => void;
  choices?: FieldChoices;
}) {
  const isNull = value === null;
  const text = isNull ? '' : asString(value);

  /** 可空字段的"清空"入口。没有它，客户没法把一个字段从有值改回空 */
  const clearAction =
    field.nullable && !isNull ? (
      <button type="button" className="bfield__clear" onClick={() => onChange(null)}>
        清空
      </button>
    ) : null;

  if (field.type === 'boolean') {
    return (
      <FieldShell field={field}>
        <label className="check">
          <input
            type="checkbox"
            checked={value === true}
            onChange={(e) => onChange(e.target.checked)}
          />
          <span>开启</span>
        </label>
      </FieldShell>
    );
  }

  if (field.type === 'select') {
    const options = resolveOptions(field, choices);
    /** 与 option.value 同构的当前值。numeric 字段必须走这条，否则显示不出已选值 */
    const selected = asOptionValue(value);

    // 动态选项拿不到（接口失败、站点还没有分类）时退回文本输入，
    // 而不是渲染一个没有选项的下拉 —— 空下拉会被读成"这里没有可选项"
    if (options === null) {
      return (
        <FieldShell field={field} action={clearAction}>
          <input
            className="field"
            value={selected}
            maxLength={field.maxLength}
            placeholder="（暂时读不到可选列表，可直接填写）"
            onChange={(e) =>
              onChange(e.target.value === '' && field.nullable ? null : e.target.value)
            }
          />
        </FieldShell>
      );
    }

    return (
      // 不再额外挂「清空」按钮：下面的「（未设置）」选项就是清空入口，
      // 两个清空入口并排会让客户以为它们不等价
      <FieldShell field={field}>
        <select
          className="field"
          value={selected}
          onChange={(e) =>
            onChange(
              e.target.value === '' && field.nullable
                ? null
                : field.numeric
                  ? Number(e.target.value)
                  : e.target.value,
            )
          }
        >
          {/* 值不在选项里时给一个占位项：否则浏览器会静默显示第一项，
              客户以为"已经是这个值了"，保存后却被改成了别的 */}
          {selected === '' || !options.some((o) => o.value === selected) ? (
            <option value="">（未设置）</option>
          ) : null}
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </FieldShell>
    );
  }

  if (field.type === 'number') {
    return (
      <FieldShell field={field}>
        <input
          className="field"
          type="number"
          value={isNull ? '' : asNumber(value)}
          min={field.min}
          max={field.max}
          step={field.step ?? 1}
          onChange={(e) => {
            const raw = e.target.value;
            // 清空输入框时不要写 NaN —— 那会让整个块过不了 schema，
            // 而错误提示只会指向"保存失败"，客户找不到原因
            onChange(raw === '' ? (field.nullable ? null : 0) : Number(raw));
          }}
        />
      </FieldShell>
    );
  }

  if (field.type === 'textarea') {
    return (
      <FieldShell field={field} action={clearAction}>
        <textarea
          className="field"
          rows={(field.maxLength ?? 0) > 1000 ? 10 : 4}
          value={text}
          maxLength={field.maxLength}
          placeholder={isNull ? '（空）' : undefined}
          onChange={(e) => onChange(e.target.value)}
        />
      </FieldShell>
    );
  }

  if (field.type === 'image') {
    const media = choices?.media;
    /**
     * 要不要给这一格接 AI：**只看字段描述符有没有 `artAspect`**。
     *
     * 不在控制台按区块类型写一张白名单：白名单会和版式漂移（新加一个首屏区块，
     * 忘了更新控制台，客户就发现"这个区块没有 AI 按钮"却不知道为什么）。
     * 而 `artAspect` 就写在字段定义旁边，加字段的人一眼能看见。
     */
    const art =
      media && media.canGenerateArt === true && field.artAspect
        ? {
            tenantId: media.tenantId,
            siteId: media.siteId,
            aspect: field.artAspect,
          }
        : undefined;
    return (
      <FieldShell field={field} action={clearAction}>
        {/*
          统一走 ImageField：预览、地址、媒体库、上传四件事在一个组件里。
          之前这里只有一个文本框，客户拿着一张刚传过的图也只能去媒体库
          复制链接再粘回来 —— 而"复制链接"这个动作本身就不该存在。
        */}
        <ImageField
          value={text}
          onChange={onChange}
          tenantId={choices?.media?.tenantId}
          siteId={choices?.media?.siteId}
          placeholder="图片地址，或留空"
          uploadLabel="上传图片"
          maxLength={field.maxLength ?? 500}
          showRemove={false}
          {...(art ? { art } : {})}
        />
      </FieldShell>
    );
  }

  /*
   * 资料选择器。
   *
   * 值本身是资料 uuid，客户不可能手抄，所以**只要拿得到列表就一定走下拉**。
   * 三档退化，顺序很重要：
   *  1. `undefined` = 列表还没拉回来 / 拉失败了 → 退回文本输入（保留已存的值，
   *     也允许清空）。给空白下拉会被读成"资料库是空的"，于是客户跑去再传一份；
   *  2. 拿到但为空 → 明确说"资料库还没有文件"，而不是给一个只有「（未设置）」的下拉；
   *  3. 有列表 → 正常下拉，value 是 uuid、label 是文件名 + 大小（同一份文件
   *     传了两版时，只看名字分不出来）。
   */
  if (field.type === 'resource') {
    const options = choices?.resource;

    if (options === undefined) {
      return (
        <FieldShell field={field} action={clearAction}>
          <input
            className="field"
            value={text}
            placeholder="（暂时读不到资料列表，可按 ID 填写）"
            onChange={(e) => onChange(e.target.value)}
          />
        </FieldShell>
      );
    }

    if (options.length === 0) {
      return (
        <FieldShell field={field} action={clearAction}>
          <p className="muted">
            「资料库」里还没有文件。先到左侧「资料库」上传一份 PDF，再回来这里选。
          </p>
          {text ? (
            <p className="muted">
              当前填的是：<code>{text}</code>
            </p>
          ) : null}
        </FieldShell>
      );
    }

    return (
      <FieldShell field={field}>
        <select className="field" value={text} onChange={(e) => onChange(e.target.value)}>
          <option value="">（未选择）</option>
          {/* 已选的资料被删掉/停用后就不在列表里了。补一个占位项，
              否则浏览器会静默显示第一份文件，客户保存后就被换成了别的东西 */}
          {text !== '' && !options.some((o) => o.value === text) ? (
            <option value={text}>（已失效的资料，请重新选择）</option>
          ) : null}
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </FieldShell>
    );
  }

  return (
    <FieldShell field={field} action={clearAction}>
      <input
        className="field"
        value={text}
        maxLength={field.maxLength}
        placeholder={isNull ? '（空）' : field.placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </FieldShell>
  );
}

/** 纯字符串数组：一行一个，可增删 */
function StringListField({
  field,
  value,
  onChange,
  choices,
}: {
  field: Extract<AnyBlockField, { type: 'stringList' }>;
  value: unknown;
  onChange: (next: unknown) => void;
  choices?: FieldChoices;
}) {
  const items = Array.isArray(value) ? value.map((v) => String(v)) : [];
  /**
   * 存量坏数据：这个位置曾经被写成一个字符串（老版本把数组字段渲染成文本框，
   * 见 `ScalarField` 上面那段）。直接当成空数组会让客户"上一秒填的字不见了"，
   * 再保存一次就永久丢掉。所以**把那段字当成唯一一条**显示出来，
   * 客户能看见、能改、能删，形状在他下一次编辑时自己就修正了。
   */
  const legacy = !Array.isArray(value) && typeof value === 'string' && value !== '';
  const shown = legacy ? [value] : items;
  const max = field.max ?? 99;
  // 有 optionsSource 就把每行换成下拉：客户选「筒灯」而不是抄 `downlights`。
  // 拿不到列表时退回输入框（options === null），不至于填不了
  const options = field.optionsSource ? resolveOptions(field, choices) : null;

  return (
    <FieldShell
      field={field}
      action={
        <button
          type="button"
          className="bfield__clear"
          disabled={shown.length >= max}
          onClick={() => onChange([...shown, ''])}
        >
          ＋ 添加
        </button>
      }
    >
      {shown.length === 0 ? (
        <p className="muted">还没有条目。</p>
      ) : (
        <ul className="bfield__rows">
          {shown.map((item, index) => (
            <li key={index} className="bfield__row">
              {options ? (
                <select
                  className="field"
                  value={item}
                  aria-label={`第 ${index + 1} 个分类`}
                  onChange={(e) => {
                    const next = [...shown];
                    next[index] = e.target.value;
                    onChange(next);
                  }}
                >
                  {/* 已选项不在当前分类列表里（分类被删了 / 改了 slug）时
                      仍要能显示出来，否则下拉会静默跳到第一项，
                      保存后这条配置就被改成客户没选过的值 */}
                  {item === '' || !options.some((o) => o.value === item) ? (
                    <option value="">（未选择）</option>
                  ) : null}
                  {options.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  className="field"
                  value={item}
                  placeholder={field.placeholder}
                  onChange={(e) => {
                    const next = [...shown];
                    next[index] = e.target.value;
                    onChange(next);
                  }}
                />
              )}
              <button
                type="button"
                className="iconbtn"
                title="删除这一条"
                aria-label={`删除第 ${index + 1} 条`}
                onClick={() => onChange(shown.filter((_, i) => i !== index))}
              >
                ✕
              </button>
            </li>
          ))}
        </ul>
      )}
    </FieldShell>
  );
}

/** 对象数组：每条可以展开编辑、可以删、可以加 */
function ObjectListField({
  field,
  value,
  onChange,
  choices,
}: {
  field: Extract<AnyBlockField, { type: 'list' }>;
  value: unknown;
  onChange: (next: unknown) => void;
  choices?: FieldChoices;
}) {
  const items = Array.isArray(value)
    ? value.map((item) => (typeof item === 'object' && item !== null ? (item as Props) : {}))
    : [];
  const max = field.max ?? 99;
  // 展开状态自己管：加了新条目要自动展开，否则客户点完"添加"看不到任何变化
  const [openIndex, setOpenIndex] = useState<number | null>(items.length > 0 ? 0 : null);

  const titleOf = (item: Props, index: number): string => {
    const raw = field.titleKey ? item[field.titleKey] : undefined;
    const text = typeof raw === 'string' ? raw.trim() : '';
    return text || `第 ${index + 1} 条`;
  };

  return (
    <FieldShell
      field={field}
      action={
        <button
          type="button"
          className="bfield__clear"
          disabled={items.length >= max}
          onClick={() => {
            const next = [...items, structuredClone(field.defaultItem)];
            onChange(next);
            setOpenIndex(next.length - 1);
          }}
        >
          ＋ 添加{field.itemLabel}
        </button>
      }
    >
      {items.length === 0 ? (
        <p className="muted">还没有条目。</p>
      ) : (
        <ul className="bfield__rows">
          {items.map((item, index) => (
            <li key={index} className="bfield__item">
              <div className="bfield__itemhead">
                <button
                  type="button"
                  className="bfield__toggle"
                  aria-expanded={openIndex === index}
                  onClick={() => setOpenIndex(openIndex === index ? null : index)}
                >
                  <span className="bfield__caret" aria-hidden="true">
                    {openIndex === index ? '▾' : '▸'}
                  </span>
                  {titleOf(item, index)}
                </button>
                <button
                  type="button"
                  className="iconbtn"
                  title="删除这一条"
                  aria-label={`删除「${titleOf(item, index)}」`}
                  onClick={() => {
                    onChange(items.filter((_, i) => i !== index));
                    setOpenIndex(null);
                  }}
                >
                  ✕
                </button>
              </div>
              {openIndex === index ? (
                <div className="fields bfield__itembody">
                  {/*
                    必须走 `FieldControl` 而不是 `ScalarField`。
                    条目里可以再嵌字符串数组（`tabs[].bullets`）甚至再一层对象数组，
                    用 `ScalarField` 渲染它们只会得到一个文本框，一敲就把数组写成字符串，
                    整块随即过不了 schema —— 预览里那一段会静默换成主题的演示文案。
                  */}
                  {field.fields.map((sub) => (
                    <FieldControl
                      key={sub.key}
                      field={sub}
                      choices={choices}
                      value={item[sub.key]}
                      onChange={(next) => {
                        const copy = [...items];
                        copy[index] = { ...item, [sub.key]: next };
                        onChange(copy);
                      }}
                    />
                  ))}
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </FieldShell>
  );
}

export function FieldControl({
  field,
  value,
  onChange,
  choices,
}: {
  field: AnyBlockField;
  value: unknown;
  onChange: (next: unknown) => void;
  choices?: FieldChoices;
}) {
  if (field.type === 'stringList') {
    return <StringListField field={field} value={value} onChange={onChange} choices={choices} />;
  }
  if (field.type === 'list') {
    return <ObjectListField field={field} value={value} onChange={onChange} choices={choices} />;
  }
  return <ScalarField field={field} value={value} onChange={onChange} choices={choices} />;
}

/** 整块属性表单：按描述符逐字段渲染 */
export function BlockPropsEditor({
  fields,
  value,
  onChange,
  choices,
  onFieldFocus,
}: {
  fields: AnyBlockField[];
  value: Props;
  onChange: (next: Props) => void;
  choices?: FieldChoices;
  /**
   * 某个字段获得焦点时回调它的 key。
   *
   * 用事件委托而不是给每个控件塞 onFocus：字段控件有七八种（输入框、下拉、
   * 复选、列表行、对象数组），逐个加会漏，而且新加一种控件就再漏一次。
   * React 的 focus 会冒泡，所以在容器上捕获一次就够。
   */
  onFieldFocus?: (key: string) => void;
}) {
  return (
    <div
      className="fields"
      onFocusCapture={
        onFieldFocus
          ? (event) => {
              const holder = (event.target as HTMLElement).closest('[data-field]');
              const key = holder?.getAttribute('data-field');
              if (key) onFieldFocus(key);
            }
          : undefined
      }
    >
      {fields.map((field) => (
        <FieldControl
          key={field.key}
          field={field}
          choices={choices}
          value={value[field.key]}
          onChange={(next) => onChange({ ...value, [field.key]: next })}
        />
      ))}
    </div>
  );
}

/** 描述符缺失时的兜底：只提示"能改但不完整"，不做二次渲染 */
export function MissingFieldsNotice({ type }: { type: string }) {
  return (
    <p className="muted">
      区块 <code>{type}</code>{' '}
      还没有字段描述，下面是通用编辑器：字段名是英文标识，枚举与列表只能手改。
    </p>
  );
}
